import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { useSiteSettings } from '../App';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { siteSettings } = useSiteSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast.error('Invalid reset link');
      navigate('/');
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.password
      });
      
      toast.success('Password reset successfully! You can now login.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: siteSettings.login_background 
          ? `url(${BACKEND_URL}${siteSettings.login_background})`
          : 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card p-8 fade-in">
          <div className="text-center mb-8">
            {siteSettings.site_logo ? (
              <>
                <div className="mb-4 p-6 bg-white rounded-xl inline-flex items-center justify-center shadow-lg min-w-[300px] min-h-[140px]">
                  <img 
                    src={`${BACKEND_URL}${siteSettings.site_logo}`} 
                    alt={siteSettings.site_title}
                    className="max-h-32 max-w-[280px] object-contain"
                  />
                </div>
                {siteSettings.site_title && siteSettings.site_title !== 'BuildTrack' && (
                  <h2 className="text-2xl font-bold text-white mb-2">{siteSettings.site_title}</h2>
                )}
              </>
            ) : (
              siteSettings.site_title && siteSettings.site_title !== 'BuildTrack' && (
                <h1 className="text-4xl font-bold text-white mb-2">{siteSettings.site_title}</h1>
              )
            )}
            <h2 className="text-xl font-semibold text-white mt-4">Reset Your Password</h2>
            <p className="text-slate-400 text-sm mt-2">Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-slate-300">New Password</Label>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
                minLength={6}
              />
              <p className="text-xs text-slate-400 mt-1">Must be at least 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
                className="mt-1"
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg shadow-md transition-all min-h-[48px]"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>

            <Button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 rounded-lg shadow-md transition-all min-h-[48px]"
            >
              Back to Login
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
